import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Copy, Edit, Trash2, FileText, Layers, Puzzle } from 'lucide-react';
import { apiService } from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';

type OrigemTemplate = 'simples' | 'multiparte' | 'parte';

interface TemplateCombinado {
  id: string;
  origem: OrigemTemplate;
  nome: string;
  tipo: string;
  descricao?: string;
  norma?: string;
  ativo: boolean;
  createdAt: string;
  usos?: number;
  totalPartes?: number;
  totalRiscos?: number;
  grupoNome?: string;
  tipoMaquina?: string;
}

export function TemplatesList() {
  const { projeto } = useProjeto();
  const { t, formatarData } = useI18n();
  const [templatesSimples, setTemplatesSimples] = useState<any[]>([]);
  const [templatesMultiparte, setTemplatesMultiparte] = useState<any[]>([]);
  const [templatesParte, setTemplatesParte] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroCategoria, setFiltroCategoria] = useState<string>('');
  const [filtroStatus, setFiltroStatus] = useState<string>('');
  const [filtroOrigem, setFiltroOrigem] = useState<string>('');
  const navigate = useNavigate();
  const [busca, setBusca] = useState('');

  useEffect(() => {
    if (projeto) {
      carregarTemplates();
    }
  }, [projeto, filtroCategoria, filtroStatus, busca]);

  const carregarTemplates = async () => {
    if (!projeto) return;

    setLoading(true);
    try {
      const paramsSimples: any = {
        projetoId: projeto.id,
        page: 1,
        limit: 100
      };

      const paramsMultiparte: any = {
        projetoId: projeto.id,
        page: 1,
        limit: 100
      };

      if (filtroCategoria) {
        paramsSimples.tipo = filtroCategoria;
        paramsMultiparte.tipoMaquina = filtroCategoria;
      }

      if (filtroStatus) {
        paramsSimples.ativo = filtroStatus === 'ativo';
        paramsMultiparte.ativo = filtroStatus === 'ativo';
      }

      if (busca) {
        paramsSimples.busca = busca;
        paramsMultiparte.busca = busca;
      }

      const ativoParte = filtroStatus ? filtroStatus === 'ativo' : undefined;

      const [respSimples, respMultiparte, respParte] = await Promise.all([
        apiService.listarTemplates(paramsSimples),
        apiService.listarTemplateMultiPartes(paramsMultiparte),
        apiService.listarTemplatePartes(projeto.id, busca || undefined, ativoParte)
      ]);

      if (respSimples.success && respSimples.data) {
        setTemplatesSimples(Array.isArray(respSimples.data) ? respSimples.data : []);
      } else {
        setTemplatesSimples([]);
      }

      if (respMultiparte.success && respMultiparte.data) {
        setTemplatesMultiparte(Array.isArray(respMultiparte.data) ? respMultiparte.data : []);
      } else {
        setTemplatesMultiparte([]);
      }

      if (respParte.success && respParte.data) {
        setTemplatesParte(Array.isArray(respParte.data) ? respParte.data : []);
      } else {
        setTemplatesParte([]);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      setTemplatesSimples([]);
      setTemplatesMultiparte([]);
      setTemplatesParte([]);
    } finally {
      setLoading(false);
    }
  };

  const templates = useMemo<TemplateCombinado[]>(() => {
    const simples: TemplateCombinado[] = templatesSimples.map((item) => ({
      id: item.id,
      origem: 'simples' as OrigemTemplate,
      nome: item.nome,
      tipo: item.tipo || t('templates.lista.tipoNaoEspecificado'),
      descricao: item.descricao,
      norma: item.norma,
      ativo: item.ativo,
      createdAt: item.createdAt,
      usos: item.usos,
    }));

    const multi: TemplateCombinado[] = templatesMultiparte.map((item) => ({
      id: item.id,
      origem: 'multiparte' as OrigemTemplate,
      nome: item.nome,
      tipo: item.tipoMaquina || t('templates.lista.tipoNaoEspecificado'),
      descricao: item.descricao,
      norma: undefined,
      ativo: item.ativo,
      createdAt: item.createdAt,
      totalPartes: item.totalPartes,
      totalRiscos: item.totalRiscos,
      grupoNome: item.grupo?.nome,
      tipoMaquina: item.tipoMaquina,
    }));

    // Templates de parte não possuem "tipo"; ficam de fora quando há filtro de categoria
    const partes: TemplateCombinado[] = filtroCategoria ? [] : templatesParte.map((item) => ({
      id: item.id,
      origem: 'parte' as OrigemTemplate,
      nome: item.nome,
      tipo: t('templates.lista.tipoParteAvulsa'),
      descricao: item.descricao,
      norma: undefined,
      ativo: item.ativo,
      createdAt: item.createdAt,
      totalRiscos: Array.isArray(item.riscos) ? item.riscos.length : 0,
    }));

    let combinados = [...simples, ...multi, ...partes];

    if (filtroOrigem) {
      combinados = combinados.filter((item) => item.origem === filtroOrigem);
    }

    return combinados.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [templatesSimples, templatesMultiparte, templatesParte, filtroOrigem, filtroCategoria, t]);

  const handleCriarTemplate = () => {
    navigate('/meus-templates/novo');
  };

  const handleDuplicar = async (template: TemplateCombinado) => {
    if (template.origem === 'multiparte') return;

    const novoNome = prompt(
      t('templates.lista.duplicarPrompt'),
      t('templates.lista.duplicarSugestao', { nome: template.nome })
    );

    if (!novoNome) return;

    try {
      const response = await apiService.duplicarTemplate(template.id, novoNome);

      if (response.success) {
        alert(t('templates.lista.duplicarSucesso'));
        carregarTemplates();
      } else {
        alert(t('templates.lista.duplicarErroDetalhe', {
          mensagem: response.message || t('templates.estado.erroDesconhecido'),
        }));
      }
    } catch (error) {
      console.error('Erro ao duplicar template:', error);
      alert(t('templates.lista.duplicarErro'));
    }
  };

  const handleRemover = async (template: TemplateCombinado) => {
    const confirmacao = template.ativo
      ? t('templates.lista.removerConfirmarDesativar', { nome: template.nome })
      : t('templates.lista.removerConfirmarRemover', { nome: template.nome });

    if (!confirm(confirmacao)) {
      return;
    }

    try {
      const response = template.origem === 'multiparte'
        ? await apiService.removerTemplateMultiParte(template.id)
        : template.origem === 'parte'
          ? await apiService.removerTemplateParte(template.id)
          : await apiService.removerTemplate(template.id);

      if (response.success) {
        alert(t('templates.lista.removerSucesso'));
        carregarTemplates();
      } else {
        alert(t('templates.lista.removerErroDetalhe', {
          mensagem: response.message || t('templates.estado.erroDesconhecido'),
        }));
      }
    } catch (error) {
      console.error('Erro ao remover template:', error);
      alert(t('templates.lista.removerErro'));
    }
  };

  const handleEditar = (template: TemplateCombinado) => {
    if (template.origem === 'multiparte') {
      navigate(`/meus-templates/multiparte/${template.id}/editar`);
      return;
    }

    if (template.origem === 'parte') {
      navigate(`/meus-templates/parte/${template.id}/editar`);
      return;
    }

    navigate(`/meus-templates/${template.id}/editar`);
  };

  const categorias = useMemo(() => {
    const tiposSimples = templatesSimples.map((item: any) => item.tipo).filter(Boolean);
    const tiposMulti = templatesMultiparte.map((item: any) => item.tipoMaquina).filter(Boolean);
    return Array.from(new Set([...tiposSimples, ...tiposMulti]));
  }, [templatesSimples, templatesMultiparte]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-schmersal-blue">{t('templates.lista.titulo')}</h1>
          <p className="mt-1 text-sm text-schmersal-gray-medium">
            {t('templates.lista.subtitulo')}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <button
            onClick={() => navigate('/meus-templates/modelos')}
            className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2.5 border border-schmersal-blue rounded-lg 
                     shadow-sm text-sm font-semibold text-schmersal-blue bg-white 
                     hover:bg-blue-50 focus:outline-none focus:ring-2 
                     focus:ring-offset-2 focus:ring-schmersal-blue transition-all duration-200"
          >
            {t('templates.lista.modelosPartes')}
          </button>

          <button
            onClick={handleCriarTemplate}
            className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2.5 border-2 border-transparent rounded-lg 
                     shadow-md text-sm font-semibold text-white bg-schmersal-blue 
                     hover:bg-schmersal-blue-dark focus:outline-none focus:ring-2 
                     focus:ring-offset-2 focus:ring-schmersal-blue transition-all duration-200"
          >
            <Plus className="h-5 w-5 mr-2" />
            {t('templates.lista.novoTemplate')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('comum.acao.buscar')}</label>
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder={t('templates.lista.filtroBuscaPlaceholder')}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('comum.rotulo.tipo')}</label>
            <select
              value={filtroOrigem}
              onChange={(e) => setFiltroOrigem(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="">{t('comum.rotulo.todos')}</option>
              <option value="simples">{t('templates.lista.origemSimples')}</option>
              <option value="multiparte">{t('templates.lista.origemMultiparte')}</option>
              <option value="parte">{t('templates.lista.origemParte')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('templates.lista.filtroCategoria')}</label>
            <select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="">{t('comum.rotulo.todas')}</option>
              {categorias.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('comum.rotulo.status')}</label>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="">{t('comum.rotulo.todos')}</option>
              <option value="ativo">{t('comum.estado.ativo')}</option>
              <option value="inativo">{t('comum.estado.inativo')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-sm text-gray-500">{t('templates.lista.carregando')}</p>
          </div>
        </div>
      )}

      {/* Templates Grid */}
      {!loading && (
        <>
          {templates.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-12 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('templates.lista.vazioTitulo')}</h3>
              <p className="text-sm text-gray-500 mb-4">
                {busca || filtroCategoria || filtroStatus
                  ? t('templates.lista.vazioComFiltro')
                  : t('templates.lista.vazioSemFiltro')}
              </p>
              {!busca && !filtroCategoria && !filtroStatus && (
                <button
                  onClick={handleCriarTemplate}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('templates.lista.criarTemplate')}
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => (
                <div key={`${template.origem}:${template.id}`} className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {template.origem === 'multiparte' ? (
                          <Layers className="h-8 w-8 text-indigo-500" />
                        ) : template.origem === 'parte' ? (
                          <Puzzle className="h-8 w-8 text-amber-500" />
                        ) : (
                          <FileText className="h-8 w-8 text-blue-500" />
                        )}
                        <div className="ml-4">
                          <h3 className="text-lg font-medium text-gray-900">
                            {template.nome}
                          </h3>
                          <p className="text-sm text-gray-500">{template.tipo}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${template.ativo
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {template.ativo ? t('comum.estado.ativo') : t('comum.estado.inativo')}
                        </span>
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full ${template.origem === 'multiparte'
                          ? 'bg-indigo-100 text-indigo-700'
                          : template.origem === 'parte'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-emerald-100 text-emerald-700'
                          }`}>
                          {template.origem === 'multiparte'
                            ? t('templates.lista.badgeMaquina')
                            : template.origem === 'parte'
                              ? t('templates.lista.badgeParte')
                              : t('templates.lista.badgeSimples')}
                        </span>
                      </div>
                    </div>

                    <p className="mt-4 text-sm text-gray-600">
                      {template.descricao || t('templates.lista.semDescricao')}
                    </p>

                    <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                      {template.origem === 'multiparte' ? (
                        <span>{t('templates.lista.contagemPartesRiscos', { partes: template.totalPartes || 0, riscos: template.totalRiscos || 0 })}</span>
                      ) : template.origem === 'parte' ? (
                        <span>{t('templates.lista.contagemRiscos', { riscos: template.totalRiscos || 0 })}</span>
                      ) : (
                        <span>{t('templates.lista.contagemUsos', { total: template.usos || 0 })}</span>
                      )}
                      <span>{formatarData(template.createdAt)}</span>
                    </div>

                    {template.origem === 'multiparte' && template.grupoNome && (
                      <div className="mt-2 text-xs text-gray-400">
                        {t('templates.lista.grupo', { nome: template.grupoNome })}
                      </div>
                    )}

                    {template.origem === 'simples' && template.norma && (
                      <div className="mt-2 text-xs text-gray-400">
                        {t('templates.lista.norma', { norma: template.norma })}
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-50 px-6 py-3">
                    <div className="flex space-x-3">
                      {template.origem === 'simples' && (
                        <button
                          onClick={() => handleDuplicar(template)}
                          className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          {t('comum.acao.duplicar')}
                        </button>
                      )}
                      <button
                        onClick={() => handleEditar(template)}
                        className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        {t('comum.acao.editar')}
                      </button>
                      <button
                        onClick={() => handleRemover(template)}
                        className="inline-flex justify-center items-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                        title={template.ativo ? t('templates.lista.acaoDesativar') : t('templates.lista.acaoRemover')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}



      {/* Create Template Guide */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('templates.lista.guiaTitulo')}
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <h4 className="text-sm font-medium text-gray-900">{t('templates.lista.guiaPasso1Titulo')}</h4>
            <p className="mt-1 text-sm text-gray-500">
              {t('templates.lista.guiaPasso1Texto')}
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Edit className="h-6 w-6 text-green-600" />
            </div>
            <h4 className="text-sm font-medium text-gray-900">{t('templates.lista.guiaPasso2Titulo')}</h4>
            <p className="mt-1 text-sm text-gray-500">
              {t('templates.lista.guiaPasso2Texto')}
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Plus className="h-6 w-6 text-purple-600" />
            </div>
            <h4 className="text-sm font-medium text-gray-900">{t('templates.lista.guiaPasso3Titulo')}</h4>
            <p className="mt-1 text-sm text-gray-500">
              {t('templates.lista.guiaPasso3Texto')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
